<template>
    <BlocksCLHero :id="props.id" v-bind="computedComponent">
        <template #buttons>
            <button
                class="ds-btn ds-btn--primary ds-btn--large"
                @click="startGroupOrder"
            >
                <span>{{ t('buttons.order') }}</span>
                <DSIcon size="s" code="default-fill/chevron-right" />
            </button>
        </template>
    </BlocksCLHero>
    <div>
        <Teleport v-if="modal.name === 'group-order-form'" :to="modal.modalTarget">
            <div class="ds-modal-content size-small">
                <div class="header">
                    <h5 class="h6 uppercase">{{ stepTitle }}</h5>
                    <button
                        @click="modal.unsetNameAndClose(); reset()"
                    >
                        <DSIcon
                            code="close"
                            size="m"
                        />
                    </button>
                </div>
                <div class="body">
                    <template v-if="step === 5">
                        <h2 class="h2">{{ t('summary') }}</h2>
                        <p>{{ t('text1') }}</p>
                        <p>{{ t('text2') }}</p>
                    </template>
                    <BlocksGroupOrderSummary :step="step" :station="station" />
                    <BlocksGroupOrderStepsStep1 v-if="step === 1" :stations="data" />
                    <BlocksGroupOrderStepsStep2 v-else-if="step === 2" :station="station" />
                    <BlocksGroupOrderStepsStep3 v-else-if="step === 3" />
                    <BlocksGroupOrderStepsStep4 v-else-if="step === 4" />
                    <BlocksGroupOrderStepsStep5 v-else-if="step === 5" />
                    <BlocksGroupOrderStepsStep6 v-else-if="step === 6" :station="station" />
                    <BlocksGroupOrderStepsCancel v-else />
                </div>
                <div class="footer">
                    <template v-if="step < 6">
                        <button
                            v-if="step > 1"
                            class="ds-btn ds-btn--large ds-btn--secondary"
                            @click="prevStep()"
                        >
                            {{ t('buttons.back') }}
                        </button>
                        <button
                            class="ds-btn ds-btn--large ds-btn--secondary"
                            @click="cancelGroupOrder"
                        >
                            {{ t('buttons.cancel') }}
                        </button>
                    </template>
                    <button
                        class="ds-btn ds-btn--large ds-btn--primary"
                        :disabled="validStep"
                        @click="nextStep()"
                    >
                        {{ nextButtonText }}
                    </button>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<script setup>
import { ref, provide, computed, useNuxtApp, useI18n, useFormData } from '#imports';
import { useModalStore } from '~dsc/stores/modal';
import { useNotificationsStore } from '~dsc/stores/notifications';

const modal = useModalStore();
const { $paths } = useNuxtApp();

defineOptions({
    inheritAttrs: false,
});

const formData = useFormData([
    { key: 'station', value: null, validators: ['notEmpty'] },
    { key: 'date', value: null, validators: ['notEmpty'] },
    { key: 'time', value: '14:00' },
    { key: 'persons', value: null, validators: ['notEmpty', { type: 'minValue', rule: { value: 8 } }] },
    { key: 'food', value: null, validators: ['notEmpty', { type: 'maxLength', rule: { value: 1000 } }] },
    { key: 'allergies', value: null, validators: ['notEmpty', { type: 'maxLength', rule: { value: 1000 } }] },
    { key: 'contact', value: null, validators: ['notEmpty'] },
    { key: 'phone', value: null, validators: ['notEmpty', 'phone'] },
    { key: 'email', value: null, validators: ['notEmpty', 'email'] },
    { key: 'contactMethod', value: 'phone' },
    { key: 'terms', value: null, validators: ['notEmpty'] },
]);

const step = ref(1);
const previousStep = ref(null);
const steps = ref(5);
const loading = ref(false);

const props = defineProps({
    data: {
        type: Array,
        required: false,
        default: () => [],
    },
});

provide('order', formData);

const station = computed(() => {
    const station = props.data.find(station => station.id === formData.data.value.station);
    if (station) return station;

    return null;
});

const validStep = computed(() => {
    if (step.value === 1 && formData.isValid('station')) {
        return false;
    }
    if (step.value === 2 && formData.isValid('date') && formData.isValid('persons') && formData.data.value.persons >= 8) {
        return false;
    }
    if (step.value === 3 && formData.isValid('food') && formData.isValid('allergies')) {
        return false;
    }
    if (step.value === 4 && formData.isValid('contact') && formData.isValid('phone') && formData.isValid('email')) {
        return false;
    }
    if (step.value === 5 && formData.isValid('terms')) {
        return false;
    }
    if (step.value === 6) {
        return false;
    }
    if (step.value === 0 || step.value === null) {
        return false;
    }

    return true;
});

const computedComponent = computed(() => ({
    cards: [
        {
            contentType: 'card',
            image: [
                {
                    ext: 'jpg',
                    width: 3600,
                    height: 2700,
                    generic_url: 'https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoic3QxXC9maWxlXC90MUZlNWFja3NDRDEzblIybnNGcS5qcGcifQ:st1:EyJX0o8rSaeOWwT70dZXtHJENjGizXZ6Z1vudeiTrsA?width={width}',
                    fill: 'cover',
                },
            ],
            alt: 'HelmiSimpukan ruokalista tarjoaa astetta parempaa huoltoasemaruokaa.',
            text: {
                nodeType: 'document',
                content: [
                    {
                        nodeType: 'heading-2',
                        content: [
                            {
                                nodeType: 'text',
                                value: 'Tee ryhmätilaus ennakkoon',
                                marks: [],
                            },
                        ],
                    },
                    {
                        nodeType: 'paragraph',
                        content: [
                            {
                                nodeType: 'text',
                                value: 'HelmiSimpukassa ryhmäsi syö edullisesti ja sujuvasti ennakkotilauksella. Tutustu herkulliseen annosvalikoimaan ja tilaa ennakkoon eväät ryhmämatkalle, pelireissulle tai vaikka firman palaveriin.',
                                marks: [],
                            },
                        ],
                    },
                ],
            },
        },
    ],
    display: {
        type: 'horizontal',
        alignment: 'left',
        component: 'hero',
        background: 'primary',
    },
}));

const { t } = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            buttons: {
                send: 'Lähetä',
                back: 'Takaisin',
                continue: 'Jatka',
                cancel: 'Keskeytä',
                next: 'Seuraava',
                close: 'Sulje',
                order: 'Tilaa tästä',
            },
            groupOrdering: 'Ryhmätilaukset',
            step: 'Vaihe',
            required: 'Pakollinen',
            summary: 'Yhteenveto',
            text1: 'Maksu tapahtuu paikan päällä.',
            text2: 'Asema on yhteydessä, kun tilaus on käsitelty ja vahvistaa vielä tilauksen',
            orderFailed: 'Tilauksen lähettäminen epäonnistui. Yritä uudelleen tai ota yhteyttä asiakaspalveluun.',
        },
    },
});

const nextButtonText = computed(() => {
    if (step.value === 6) {
        return t('buttons.close');
    } else if (step.value === 5) {
        return t('buttons.send');
    } else if (!step.value) {
        return t('buttons.continue');
    }

    return t('buttons.next');
});

const stepTitle = computed(() => {
    if (step.value !== 0 && step.value <= steps.value) {
        return `${t('groupOrdering')}, ${t('step')} ${step.value}/${steps.value}`;
    }

    return t('groupOrdering');
});

function startGroupOrder() {
    step.value = 1;
    modal.setNameAndOpen('group-order-form');
}

function cancelGroupOrder() {
    if (step.value === null) {
        modal.unsetNameAndClose();
        reset();

        return;
    }

    previousStep.value = step.value;
    step.value = null;
}

async function nextStep() {
    if (!step.value) {
        step.value = previousStep.value;
        previousStep.value = null;

        return;
    }

    if (step.value === 5) {
        await sendOrder();

        return;
    } else if (step.value === 6) {
        modal.unsetNameAndClose();
        reset();

        return;
    }

    step.value = step.value + 1;
}

function prevStep() {
    step.value = step.value - 1;
}

function reset() {
    previousStep.value = null;
    step.value = 1;
    formData.reset();
}

async function sendOrder() {
    loading.value = true;
    const body = {
        stationId: station.value.id,
        customer: {
            name: formData.data.value.contact,
            email: formData.data.value.email,
            phone: formData.data.value.phone,
            contactMethod: formData.data.value.contactMethod,
        },
        order: {
            type: 'group',
            details: formData.data.value.food,
            groupSize: Number(formData.data.value.persons),
            allergenes: formData.data.value.allergies,
            deliveryDate: `${formData.data.value.date} ${formData.data.value.time}`,
        },
    };
    try {
        await $fetch(`/proxy/${$paths.getEndpoint('groupOrder')}`, {
            method: 'post',
            body,
        });

        step.value = step.value + 1;
    } catch (err) {
        useNotificationsStore().addNotification({
            type: 'error',
            message: t('orderFailed'),
        });
    }
    loading.value = false;
}
</script>
<style scoped>
.ds-modal-content .header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ds-modal-content .header .h6 {
  margin: 0;
}
.ds-modal-content .header button {
  display: inline-flex;
}
.ds-modal-content .header, .ds-modal-content .footer {
  padding: 1.5rem;
}
.ds-modal-content .footer {
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  max-width: 100%;
  padding-bottom: 0.5rem;
}
.ds-modal-content .footer button {
  margin-bottom: 1rem;
}
.ds-modal-content .footer button:not(:last-child) {
  margin-right: 1rem;
}
.ds-modal-content .body {
  padding: 0 1.5rem;
}
</style>
